import SFilter from '@/features/StructuredFilter/class/SFilter';
import { FeedbackSentiment } from '@/types/feedbackConst';
import { FEEDBACK_COLUMNS, SENTIMENT_COLUMNS } from '@/types/filterKeyConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';

import type { HierarchyNodeData } from '../components/InsightExplorerSidebar/HierarchyNavigator/HierarchyNavigator';
import { hierarchyNodeUtils } from '../components/InsightExplorerSidebar/HierarchyNavigator/utils';
import {
  InsightExplorerByType,
  InsightExplorerFieldType,
  InsightExplorerTrendViewType,
  InsightExplorerViewType,
} from '../types/constants';

const calcSentimentScore = (
  total_volume: number,
  negative_volume: number,
): number => {
  if (total_volume === 0) return 0;
  if (negative_volume === 0) return 0;
  return Number(
    (((total_volume - negative_volume) / total_volume) * 100).toFixed(1),
  );
};

const getFilterByExplorerOptions = (params: {
  view: InsightExplorerViewType;
  selectedNode: HierarchyNodeData;
  filterPayload: StructuredFilter;
  trendViewType?: InsightExplorerTrendViewType;
}): SFilter => {
  const { view, filterPayload, selectedNode, trendViewType } = params;
  const sFilter = new SFilter(filterPayload);

  switch (selectedNode.node_type) {
    case InsightExplorerByType.TAG:
      {
        const targetIds = hierarchyNodeUtils
          .getAllChildren(selectedNode)
          .map((child) => child.id);
        targetIds.push(selectedNode.id);

        sFilter.addValueSet(FEEDBACK_COLUMNS.TAG, {
          op: STRUCTURED_FILTER_OPERATOR.OR,
          values: targetIds,
        });
      }
      break;
    case InsightExplorerByType.TAXONOMY:
      {
        sFilter.addValueSet(FEEDBACK_COLUMNS.TAXONOMY, {
          op: STRUCTURED_FILTER_OPERATOR.OR,
          values: [selectedNode.id],
        });
      }
      break;
    case InsightExplorerByType.TAXONOMY_TOPIC:
      {
        sFilter.addValueSet(FEEDBACK_COLUMNS.TAXONOMY_TOPIC, {
          op: STRUCTURED_FILTER_OPERATOR.OR,
          values: [selectedNode.id],
        });
      }
      break;
    default:
      throw new Error('Invalid field type');
  }

  if (view === InsightExplorerViewType.SENTIMENTS) {
    if (trendViewType === InsightExplorerTrendViewType.NEGATIVE_VOLUME) {
      sFilter.addValueSet(SENTIMENT_COLUMNS.SENTIMENT_DETAIL, {
        op: STRUCTURED_FILTER_OPERATOR.OR,
        values: [FeedbackSentiment.NEGATIVE],
      });
    }
  }

  return sFilter;
};

const getFieldFromNodeType = (
  node_type: InsightExplorerByType,
): InsightExplorerFieldType => {
  switch (node_type) {
    case InsightExplorerByType.TAG:
      return InsightExplorerFieldType.TAG;
    case InsightExplorerByType.TAXONOMY:
      return InsightExplorerFieldType.TAXONOMY;
    case InsightExplorerByType.TAXONOMY_TOPIC:
      return InsightExplorerFieldType.TAXONOMY;
    default:
      throw new Error('Invalid node type');
  }
};

const getByFromFieldType = (
  field_type: InsightExplorerFieldType,
): InsightExplorerByType => {
  switch (field_type) {
    case InsightExplorerFieldType.TAG:
      return InsightExplorerByType.TAG;
    case InsightExplorerFieldType.TAXONOMY:
      return InsightExplorerByType.TAXONOMY;
    default:
      throw new Error('Invalid field type');
  }
};

export const insightExplorerUtils = {
  calcSentimentScore,
  getFilterByExplorerOptions,
  getFieldFromNodeType,
  getByFromFieldType,
};
